/**
 * Google Ads attribution (Cockpit, Asana 1219642275685771).
 *
 * A Google Ads click opens the funnel with gclid/gbraid/wbraid
 * (auto-tagging), gad_source/gad_campaignid and, where the account uses the
 * A&M tracking template, am_platform=google plus am_* ValueTrack values. The
 * parameters are kept in localStorage as one bundle for 90 days (first
 * touch), so a reload or a later visit still sends them with the lead as
 * `googleAds`. The backend cleans the same fields again
 * (funnelhub-backend helper/google-ads-attribution.helper.js).
 */

export const GOOGLE_ADS_STORAGE_KEY = "google_ads_attribution";
export const GOOGLE_ADS_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

const CLICK_ID_PARAMS = ["gclid", "gbraid", "wbraid"];
export const GOOGLE_ADS_PARAMS = [
  ...CLICK_ID_PARAMS,
  "gad_source",
  "gad_campaignid",
  "am_platform",
  "am_campaign_id",
  "am_campaign_name",
  "am_adgroup_id",
  "am_adgroup_name",
  "am_creative_id",
  "am_keyword",
  "am_matchtype",
  "am_device",
  "am_network"
];

const MAX_LENGTH = 512;
const LANDING_PAGE_MAX_LENGTH = 2048;
const CLICK_ID_PATTERN = /^[A-Za-z0-9._~-]{1,512}$/;
// unfilled ValueTrack parameter, e.g. {keyword}
const PLACEHOLDER_PATTERN = /^\{.*\}$/;

const cleanValue = (name, value) => {
  if (typeof value !== "string") return undefined;
  const text = value.trim();
  if (!text || text.length > MAX_LENGTH || PLACEHOLDER_PATTERN.test(text)) {
    return undefined;
  }
  if (CLICK_ID_PARAMS.includes(name) && !CLICK_ID_PATTERN.test(text)) {
    return undefined;
  }
  return text;
};

const cleanParams = (source) =>
  GOOGLE_ADS_PARAMS.reduce((params, name) => {
    const value = cleanValue(name, source(name));
    if (value !== undefined) params[name] = value;
    return params;
  }, {});

/** Allowed Google parameters of a query string (URL-decoded, trimmed). */
export const readGoogleAdsParams = (search = "") => {
  const query = new URLSearchParams(search);
  return cleanParams((name) => query.get(name) ?? undefined);
};

const hasClickId = (params) =>
  CLICK_ID_PARAMS.some((name) => Boolean(params?.[name]));

/** A Google Ads click: click id, or the am_* template with am_platform=google. */
export const hasGoogleSignal = (params) =>
  hasClickId(params) || params?.am_platform?.toLowerCase() === "google";

const getStorage = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

/** The stored bundle, if it is valid and younger than 90 days. */
export const getStoredGoogleAdsAttribution = ({
  now = Date.now(),
  storage = getStorage()
} = {}) => {
  let stored;
  try {
    stored = JSON.parse(storage?.getItem(GOOGLE_ADS_STORAGE_KEY) || "null");
  } catch {
    return null;
  }
  if (!stored || typeof stored !== "object" || Array.isArray(stored)) {
    return null;
  }
  const capturedAt = Date.parse(stored.capturedAt);
  if (Number.isNaN(capturedAt) || now - capturedAt > GOOGLE_ADS_MAX_AGE_MS) {
    return null;
  }
  const params = cleanParams((name) => stored[name]);
  if (!hasGoogleSignal(params)) return null;
  return {
    ...params,
    ...(typeof stored.landingPage === "string"
      ? { landingPage: stored.landingPage }
      : {}),
    capturedAt: stored.capturedAt
  };
};

const capture = ({
  search = window.location.search,
  href = window.location.href,
  now = Date.now(),
  storage = getStorage()
}) => {
  const stored = getStoredGoogleAdsAttribution({ now, storage });
  const params = readGoogleAdsParams(search);
  if (!hasGoogleSignal(params)) return stored;
  if (stored && (hasClickId(stored) || !hasClickId(params))) return stored;
  const bundle = {
    ...params,
    ...(href && href.length <= LANDING_PAGE_MAX_LENGTH
      ? { landingPage: href }
      : {}),
    capturedAt: new Date(now).toISOString()
  };
  try {
    storage?.setItem(GOOGLE_ADS_STORAGE_KEY, JSON.stringify(bundle));
  } catch {
    // storage full or blocked: the bundle still counts for this page
  }
  return bundle;
};

/**
 * Store the Google parameters of the current URL (first touch, 90 days) and
 * return the bundle that counts for this visitor, or null.
 * - no Google signal in the URL: the stored bundle stays
 * - stored bundle with a click id: it stays (first touch)
 * - stored bundle without a click id: replaced by a URL with a click id
 * Safe to call again at submit: it returns the same bundle, and without
 * localStorage (blocked) it still reads the current URL. Never throws:
 * attribution must not break the funnel or the lead.
 */
export const captureGoogleAdsAttribution = (options = {}) => {
  try {
    return capture(options);
  } catch {
    return null;
  }
};

/**
 * Google Ads visit: am_* values belong to Google, not to the Meta adDetails,
 * and no Facebook ad lookup may run. True with a Google signal in the URL,
 * or with a stored am_platform=google bundle when the URL carries no Meta
 * click id (a later Meta click keeps its adDetails).
 */
export const isGoogleAdsVisit = ({
  search = window.location.search,
  attribution
} = {}) => {
  if (hasGoogleSignal(readGoogleAdsParams(search))) return true;
  if (new URLSearchParams(search).get("fbclid")) return false;
  return attribution?.am_platform?.toLowerCase() === "google";
};

/**
 * Meta ad details from the am_* parameters (Meta URL template), the shape of
 * the lead's `adDetails`. Only for visits that are not Google Ads visits.
 */
export const metaAdDetailsFromParams = (searchParams = {}) => ({
  campaign_id: searchParams.am_campaign_id,
  campaign_name: searchParams.am_campaign_name,
  adset_id: searchParams.am_adset_id,
  adset_name: searchParams.am_adset_name,
  ad_id: searchParams.am_ad_id,
  ad_name: searchParams.am_ad_name
});

/**
 * On funnel load: store the Google Ads bundle (first touch) and return the
 * Meta ad data for `/funnel/ad-details`; empty on a Google Ads visit, so no
 * Facebook ad lookup runs for Google campaign ids.
 */
export const captureOnFunnelLoad = ({ searchParams, ...options } = {}) => {
  const attribution = captureGoogleAdsAttribution(options);
  return isGoogleAdsVisit({ search: options.search, attribution })
    ? {}
    : metaAdDetailsFromParams(searchParams);
};

/** `googleAds` for the lead payload; only keys with values. */
export const buildGoogleAdsPayload = (bundle) => {
  if (!bundle) return undefined;
  const payload = {
    gclid: bundle.gclid,
    gbraid: bundle.gbraid,
    wbraid: bundle.wbraid,
    gadSource: bundle.gad_source,
    campaignId: bundle.am_campaign_id || bundle.gad_campaignid,
    campaignName: bundle.am_campaign_name,
    adgroupId: bundle.am_adgroup_id,
    adgroupName: bundle.am_adgroup_name,
    creativeId: bundle.am_creative_id,
    keyword: bundle.am_keyword,
    matchtype: bundle.am_matchtype,
    device: bundle.am_device,
    network: bundle.am_network,
    landingPage: bundle.landingPage,
    capturedAt: bundle.capturedAt
  };
  Object.keys(payload).forEach((key) => {
    if (!payload[key]) delete payload[key];
  });
  return Object.keys(payload).length ? payload : undefined;
};

/**
 * Google Ads part of a lead or upcoming-lead payload: `googleAds` (the
 * upcoming lead's when the visitor comes from the reminder email, maybe on
 * another device; else this browser's first touch) and whether the visit is
 * a Google Ads visit (then no Meta adDetails from am_* values).
 */
export const getLeadGoogleAds = ({ upcomingGoogleAds, ...options } = {}) => {
  const attribution = captureGoogleAdsAttribution(options);
  return {
    googleAds: upcomingGoogleAds || buildGoogleAdsPayload(attribution),
    isGoogleVisit: isGoogleAdsVisit({ search: options.search, attribution })
  };
};

/** `tracking_data` of the lp_lead_submitted dataLayer event (null if absent). */
export const buildTrackingData = (googleAds, leadCreatedAt) => ({
  gclid: googleAds?.gclid || null,
  gbraid: googleAds?.gbraid || null,
  wbraid: googleAds?.wbraid || null,
  campaign_id: googleAds?.campaignId || null,
  adgroup_id: googleAds?.adgroupId || null,
  keyword: googleAds?.keyword || null,
  matchtype: googleAds?.matchtype || null,
  device: googleAds?.device || null,
  network: googleAds?.network || null,
  landing_page: googleAds?.landingPage || null,
  lead_created_at: leadCreatedAt || null
});
